import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface StepStatus {
  isComplete: boolean;
  hasError: boolean;
}

// Hotel configuration interface
interface HotelConfig {
  hotelId: string;
  hotelName?: string;
  apiUsername: string;
  counterId: string;
  consumerKey: string;
  consumerSecret: string;
  accessSecret: string;
  baseUrl: string;
  redisHost: string;
  redisPort: number;
  isEnabled: boolean;
}

// Add interface for Hotelogix data
interface HotelogixHotel {
  hotelId: string;
  storeId: number;
  storeAlias: string;
  hotelConfig?: HotelConfig;
}

interface ReservationSystemSetupData {
  restaurantId: string;
  storeId: number;
  reservationSystem: string;
  storeAlias: string;
}

interface ReservationState {
  reservationSystemSetup: {
    data: {
      systems: ReservationSystemSetupData[];
      hotels?: HotelogixHotel[];
      selectedSystem?: string;
      hotelConfigs?: Record<string, HotelConfig>;
    };
    status: StepStatus;
  };
  errors: Record<string, string>;
  isProfileComplete: boolean;
}

const initialState: ReservationState = {
  reservationSystemSetup: {
    data: {
      systems: [],
      hotels: [],
      selectedSystem: '',
      hotelConfigs: {},
    },
    status: { isComplete: false, hasError: false },
  },
  errors: {},
  isProfileComplete: false,
};

const reservationSlice = createSlice({
  name: 'reservation',
  initialState,
  reducers: {
    updateReservationSystemSetup: (state, action) => {
      state.reservationSystemSetup.data = {
        ...state.reservationSystemSetup.data,
        ...action.payload,
      };
    },
    clearHotelogixForStore: (state, action: PayloadAction<string>) => {
      const storeId = action.payload;

      state.reservationSystemSetup.data.systems =
        state.reservationSystemSetup.data.systems.filter(
          sys =>
            !(
              String(sys.storeId) === String(storeId) &&
              sys.reservationSystem === 'hotelogix'
            )
        );

      if (state.reservationSystemSetup.data.hotelConfigs) {
        delete state.reservationSystemSetup.data.hotelConfigs[storeId];
      }

      if (state.reservationSystemSetup.data.selectedSystem === 'hotelogix') {
        state.reservationSystemSetup.data.selectedSystem = '';
      }
    },
    updateHotelConfig: (
      state,
      action: PayloadAction<{ storeId: string; config: Partial<HotelConfig> }>
    ) => {
      const { storeId, config } = action.payload;
      if (!state.reservationSystemSetup.data.hotelConfigs) {
        state.reservationSystemSetup.data.hotelConfigs = {};
      }
      state.reservationSystemSetup.data.hotelConfigs[storeId] = {
        ...state.reservationSystemSetup.data.hotelConfigs[storeId],
        ...config,
      };
    },
    setStepStatus: (
      state,
      action: PayloadAction<{
        step: 'reservationSystemSetup';
        status: StepStatus;
      }>
    ) => {
      const { step, status } = action.payload;
      state[step].status = status;
    },
    setErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload;
    },
    clearErrors: state => {
      state.errors = {};
    },
    setProfileComplete: (state, action: PayloadAction<boolean>) => {
      state.isProfileComplete = action.payload;
    },
    clearAllStates: state => {
      Object.assign(state, initialState);
    },
  },
});

export const {
  updateReservationSystemSetup,
  clearHotelogixForStore,
  updateHotelConfig,
  setStepStatus,
  setErrors,
  clearErrors,
  setProfileComplete,
  clearAllStates,
} = reservationSlice.actions;

export default reservationSlice.reducer;
